/**
 * GIF Export — renders frames via frame-renderer then encodes with FFmpeg WASM.
 * Requires network access to load FFmpeg core from CDN (first use only; cached thereafter).
 */

import { FFmpeg } from '@ffmpeg/ffmpeg'
import { toBlobURL } from '@ffmpeg/util'
import { useEditorStore } from '@/store/editor-store'
import { useRecordingStore } from '@/store/recording-store'
import { createRenderContext, renderFrame, disposeRenderContext } from './frame-renderer'
import { ExportProgress } from './types'

export interface GifExportSettings {
    width: number
    height: number
    frameRate: number   // Recommended: 10-15 for reasonable file size
}

let ffmpegInstance: FFmpeg | null = null

async function getFFmpeg(): Promise<FFmpeg> {
    if (ffmpegInstance) return ffmpegInstance
    const ffmpeg = new FFmpeg()
    const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd'
    await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
    })
    ffmpegInstance = ffmpeg
    return ffmpeg
}

export async function exportGif(
    settings: GifExportSettings,
    onProgress: (progress: ExportProgress) => void
): Promise<Blob> {
    const editorState = useEditorStore.getState()
    const recordingState = useRecordingStore.getState()
    const videoBlob = recordingState.blob

    if (!videoBlob) throw new Error('No video to export')

    const { width, height, frameRate } = settings
    const fps = Math.max(5, Math.min(20, frameRate)) // Clamp 5-20fps for GIF

    onProgress({
        stage: 'initializing',
        progress: 0,
        currentFrame: 0,
        totalFrames: 0,
        estimatedTimeRemaining: 0,
        message: 'Loading GIF encoder…',
    })

    const ffmpeg = await getFFmpeg()

    // Load video for frame extraction
    const videoUrl = URL.createObjectURL(videoBlob)
    const video = document.createElement('video')
    video.src = videoUrl
    video.muted = true
    video.preload = 'auto'
    await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve()
        video.onerror = () => reject(new Error('Failed to load video'))
    })

    // Fix Infinity duration
    if (video.duration === Infinity) {
        video.currentTime = 1e101
        await new Promise<void>(resolve => {
            video.addEventListener('seeked', () => { video.currentTime = 0; resolve() }, { once: true })
        })
    }

    // Calculate actual export duration
    const clips = editorState.clips.filter(c => c.status === 'visible')
    const clipsEnd = clips.reduce((max, c) => Math.max(max, c.offset + (c.end - c.start) / (c.speed ?? 1)), 0)
    const duration = clipsEnd > 0 ? clipsEnd : (editorState.duration || video.duration)

    if (!Number.isFinite(duration) || duration <= 0) {
        throw new Error('Invalid video duration')
    }

    // Pre-load background image if needed
    let bgImage: ImageBitmap | null = null
    const { backgroundType, backgroundValue } = editorState
    if (backgroundType === 'image' || backgroundValue.startsWith('http') || backgroundValue.startsWith('blob:') || backgroundValue.startsWith('/')) {
        try {
            const resp = await fetch(backgroundValue)
            bgImage = await createImageBitmap(await resp.blob())
        } catch {}
    }

    const sourceWidth = video.videoWidth
    const sourceHeight = video.videoHeight
    const renderContext = createRenderContext(width, height, sourceWidth, sourceHeight, bgImage)

    const totalFrames = Math.max(1, Math.ceil(duration * fps))
    const frameDuration = 1 / fps

    const sortedClips = [...clips].sort((a, b) => a.offset - b.offset)

    // Render frames and write to FFmpeg FS
    let cameraVideo: HTMLVideoElement | null = null
    const cameraBlob = recordingState.cameraBlob
    const cameraUrl = cameraBlob ? URL.createObjectURL(cameraBlob) : null
    if (cameraBlob && cameraUrl) {
        cameraVideo = document.createElement('video')
        cameraVideo.src = cameraUrl
        cameraVideo.muted = true
        await new Promise<void>(resolve => {
            cameraVideo!.onloadedmetadata = () => resolve()
        })
    }

    const frameCanvas = new OffscreenCanvas(sourceWidth, sourceHeight)
    const frameCtx = frameCanvas.getContext('2d', { alpha: false })!

    let cameraCanvas: OffscreenCanvas | null = null
    let cameraCtx: OffscreenCanvasRenderingContext2D | null = null
    if (cameraVideo) {
        cameraCanvas = new OffscreenCanvas(cameraVideo.videoWidth, cameraVideo.videoHeight)
        cameraCtx = cameraCanvas.getContext('2d', { alpha: false })!
    }

    const startTime = performance.now()

    for (let i = 0; i < totalFrames; i++) {
        const timelineTime = i * frameDuration

        const activeClip = sortedClips.find(c => {
            const spd = c.speed ?? 1
            return timelineTime >= c.offset && timelineTime < c.offset + (c.end - c.start) / spd
        })

        let mainFrame: VideoFrame | null = null
        if (activeClip) {
            const spd = activeClip.speed ?? 1
            const sourceTime = activeClip.start + (timelineTime - activeClip.offset) * spd
            if (sourceTime <= video.duration) {
                video.currentTime = sourceTime
                await new Promise<void>(resolve => { video.onseeked = () => resolve() })
                frameCtx.drawImage(video, 0, 0)
                mainFrame = new VideoFrame(frameCanvas, { timestamp: Math.round(timelineTime * 1_000_000) })
            }
        }

        let cameraFrame: VideoFrame | null = null
        if (cameraVideo && cameraCanvas && cameraCtx) {
            const camRange = editorState.cameraLayer.timeRange
            const cameraActive = !camRange || (timelineTime >= camRange.start && timelineTime <= camRange.end)
            if (cameraActive && timelineTime <= cameraVideo.duration) {
                cameraVideo.currentTime = timelineTime
                await new Promise<void>(resolve => { cameraVideo!.onseeked = () => resolve() })
                cameraCtx.drawImage(cameraVideo, 0, 0)
                cameraFrame = new VideoFrame(cameraCanvas, { timestamp: Math.round(timelineTime * 1_000_000) })
            }
        }

        const bitmap = renderFrame(renderContext, mainFrame, timelineTime, editorState, cameraFrame)
        mainFrame?.close()
        cameraFrame?.close()

        // Convert bitmap to PNG and write to FFmpeg FS
        const tmpCanvas = new OffscreenCanvas(width, height)
        const tmpCtx = tmpCanvas.getContext('2d')!
        tmpCtx.drawImage(bitmap, 0, 0)
        bitmap.close()

        const pngBlob = await tmpCanvas.convertToBlob({ type: 'image/png' })
        const pngData = await pngBlob.arrayBuffer()
        const frameName = `frame${String(i).padStart(5, '0')}.png`
        await ffmpeg.writeFile(frameName, new Uint8Array(pngData))

        const elapsed = (performance.now() - startTime) / 1000
        const framesPerSec = (i + 1) / Math.max(0.001, elapsed)
        const remaining = Math.round((totalFrames - i - 1) / Math.max(0.001, framesPerSec))

        onProgress({
            stage: 'rendering',
            progress: ((i + 1) / totalFrames) * 75,
            currentFrame: i + 1,
            totalFrames,
            estimatedTimeRemaining: remaining,
            message: `Rendering frame ${i + 1} / ${totalFrames}…`,
        })
    }

    URL.revokeObjectURL(videoUrl)
    if (cameraUrl) URL.revokeObjectURL(cameraUrl)
    disposeRenderContext(renderContext)

    onProgress({
        stage: 'finalizing',
        progress: 80,
        currentFrame: totalFrames,
        totalFrames,
        estimatedTimeRemaining: 0,
        message: 'Building GIF palette…',
    })

    // Step 1: generate palette
    await ffmpeg.exec([
        '-framerate', String(fps),
        '-i', 'frame%05d.png',
        '-vf', `scale=${width}:${height}:flags=lanczos,palettegen=stats_mode=diff`,
        'palette.png'
    ])

    onProgress({
        stage: 'finalizing',
        progress: 90,
        currentFrame: totalFrames,
        totalFrames,
        estimatedTimeRemaining: 0,
        message: 'Encoding GIF…',
    })

    // Step 2: apply palette and encode GIF
    await ffmpeg.exec([
        '-framerate', String(fps),
        '-i', 'frame%05d.png',
        '-i', 'palette.png',
        '-lavfi', `scale=${width}:${height}:flags=lanczos [x]; [x][1:v] paletteuse=dither=bayer:bayer_scale=5`,
        'output.gif'
    ])

    const gifData = await ffmpeg.readFile('output.gif') as Uint8Array

    // Cleanup FS
    for (let i = 0; i < totalFrames; i++) {
        await ffmpeg.deleteFile(`frame${String(i).padStart(5, '0')}.png`).catch(() => {})
    }
    await ffmpeg.deleteFile('palette.png').catch(() => {})
    await ffmpeg.deleteFile('output.gif').catch(() => {})

    onProgress({
        stage: 'complete',
        progress: 100,
        currentFrame: totalFrames,
        totalFrames,
        estimatedTimeRemaining: 0,
        message: 'GIF export complete!',
    })

    return new Blob([gifData], { type: 'image/gif' })
}
